/**
 * PatientContinuationEditPage – 継続受診者登録・編集（スケジュール）
 * A2 equivalent: visit_clinic/edit/{id}/{search_yearmonth}. Full schedule edit with calendar, consent, etc.
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  AppButton,
  AppCheckbox,
  AppFormLabel,
  AppModal,
  AppSelect,
  AppSpin,
  AppTable,
  AppTextArea,
} from '@/components/atoms';
import { CollapsibleCard } from '@/components/molecules';
import { useClinicContext } from '@/hooks/main/useClinicContext';
import useAppModal from '@/hooks/useAppModal';
import {
  useContinuationEditInit,
  useContinuationDelete,
  useContinuationScheduleUpdate,
} from '@/hooks/main/useContinuationList';
import type {
  ContinuationListRow,
  VisitDataByDay,
  ConsentRow,
  RezeptPriceRow,
  PriceDataRow,
} from '@/services/main/continuation/continuationService';
import type { ColumnsType } from 'antd/es/table';

import { WEEKDAY_LABELS_BY_INDEX } from '@/constants/weekday';

type PriceTableRow = {
  key: string;
  label: string;
  initial: string;
  acupuncture: string;
  moxibustion: string;
  both: string;
  electric: string;
  house_call: string;
  house_call_excess: string;
  special: string;
  days: string;
  total: string;
};

function getPriceTableDataSource(prices: PriceDataRow): PriceTableRow[] {
  return [
    {
      key: 'price',
      label: '金額',
      initial: `${prices.initial}円`,
      acupuncture: `${prices.acupuncture}円`,
      moxibustion: `${prices.moxibustion}円`,
      both: `${prices.both}円`,
      electric: `${prices.electric}円`,
      house_call: `${prices.house_call}円`,
      house_call_excess: `${prices.house_call_excess}円`,
      special: `${prices.special_area_addition}円`,
      days: `${prices.operation_day_count}日`,
      total: `${prices.total_price}円`,
    },
    {
      key: 'count',
      label: '回数',
      initial: `${prices.initial_count}回`,
      acupuncture: `${prices.acupuncture_count}回`,
      moxibustion: `${prices.moxibustion_count}回`,
      both: `${prices.both_count}回`,
      electric: `${prices.electric_count}回`,
      house_call: `${prices.house_call_count}回`,
      house_call_excess: `${prices.house_call_excess_count}回`,
      special: `${prices.special_area_addition_count}回`,
      days: '',
      total: '',
    },
  ];
}

const PRICE_TABLE_COLUMNS: ColumnsType<PriceTableRow> = [
  {
    title: '',
    dataIndex: 'label',
    key: 'label',
    width: 56,
    render: (_, r) => (r.key === 'price' ? '金額' : '回数'),
  },
  { title: '初検', dataIndex: 'initial', key: 'initial', width: 72, align: 'right' },
  { title: 'はり', dataIndex: 'acupuncture', key: 'acupuncture', width: 72, align: 'right' },
  { title: 'きゅう', dataIndex: 'moxibustion', key: 'moxibustion', width: 72, align: 'right' },
  { title: 'はり・きゅう', dataIndex: 'both', key: 'both', width: 88, align: 'right' },
  { title: '電', dataIndex: 'electric', key: 'electric', width: 72, align: 'right' },
  { title: '往料4km迄', dataIndex: 'house_call', key: 'house_call', width: 88, align: 'right' },
  {
    title: '往料4km超',
    dataIndex: 'house_call_excess',
    key: 'house_call_excess',
    width: 88,
    align: 'right',
  },
  { title: '特別地域', dataIndex: 'special', key: 'special', width: 72, align: 'right' },
  { title: '実日数', dataIndex: 'days', key: 'days', width: 64, align: 'right' },
  { title: '合計金額', dataIndex: 'total', key: 'total', width: 88, align: 'right' },
];

const CONSENT_TABLE_COLUMNS: ColumnsType<ConsentRow> = [
  { title: 'No.', dataIndex: 'serial_number', key: 'serial_number', width: 50 },
  { title: '傷病名', dataIndex: 'injury_name', key: 'injury_name', ellipsis: true },
  { title: '同意日', dataIndex: 'consent_date', key: 'consent_date', width: 100 },
  {
    title: '初療年月日',
    dataIndex: 'first_treatment_date',
    key: 'first_treatment_date',
    width: 110,
  },
  {
    title: '要加療開始日',
    dataIndex: 'treatment_term_from',
    key: 'treatment_term_from',
    width: 110,
  },
  {
    title: '要加療終了日',
    dataIndex: 'treatment_term_to',
    key: 'treatment_term_to',
    width: 110,
  },
];

function getWeekday(yearMonth: string, day: number): number {
  const [y, m] = yearMonth.split('-').map(Number);
  const date = new Date(y, m - 1, day);
  return date.getDay();
}

/** Row config for 施術カレンダー body rows */
const CALENDAR_ROW_CONFIG: Array<{
  key: string;
  label: string;
  getVal: (v: VisitDataByDay) => unknown;
  format?: (v: VisitDataByDay) => string;
}> = [
  { key: 'initial', label: '初検', getVal: (v: VisitDataByDay) => v.is_initial_inspection_fee },
  { key: 'acupuncture', label: 'はり', getVal: (v: VisitDataByDay) => v.is_acupuncture },
  { key: 'moxibustion', label: 'きゅう', getVal: (v: VisitDataByDay) => v.is_moxibustion },
  { key: 'electric', label: '電', getVal: (v: VisitDataByDay) => v.electric_treatment },
  {
    key: 'house_call',
    label: '往診',
    getVal: (v: VisitDataByDay) => v.previous_visit_id !== -1,
  },
  {
    key: 'massager',
    label: '施術師',
    getVal: (v: VisitDataByDay) => v.massager_id,
    format: (v: VisitDataByDay) => (v.massager_id ? '他' : ''),
  },
];

type CalendarTableRow = { key: string; label: string; [day: string]: string };

const PatientContinuationEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { tenant } = useClinicContext();
  const { confirm } = useAppModal();
  const { patientId: patientIdParam, searchYearmonth } = useParams<{
    patientId: string;
    searchYearmonth: string;
  }>();
  const patientId = patientIdParam ? parseInt(patientIdParam, 10) : undefined;
  const searchYearmonthDecoded = searchYearmonth ? decodeURIComponent(searchYearmonth) : '';

  const { data, isLoading, isError, refetch } = useContinuationEditInit(
    patientId,
    searchYearmonthDecoded
  );
  const deleteMutation = useContinuationDelete();
  const scheduleUpdateMutation = useContinuationScheduleUpdate();

  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailForm, setDetailForm] = useState({
    is_initial_inspection_fee: false,
    is_acupuncture: false,
    is_moxibustion: false,
    electric_treatment: false,
    outcome_type_id: 0,
    massager_id: null as number | null,
  });

  const handleBack = useCallback(() => {
    navigate(`/${tenant}/patients/continuation`);
  }, [navigate, tenant]);

  const handleMonthChange = useCallback(
    (newYearMonth: string) => {
      if (!Number.isInteger(patientId) || !newYearMonth) return;
      navigate(
        `/${tenant}/patients/continuation/edit/${patientId}/${encodeURIComponent(newYearMonth)}`,
        { replace: true }
      );
    },
    [navigate, patientId, tenant]
  );

  const handleDelete = useCallback(
    (visitId: number) => {
      confirm({
        title: 'この受診を削除してもよろしいですか？',
        okText: 'OK',
        cancelText: 'キャンセル',
        okButtonProps: { danger: true },
        onOk: () => deleteMutation.mutate({ delete_id: visitId }, { onSuccess: () => refetch() }),
      });
    },
    [confirm, deleteMutation, refetch]
  );

  const handleDeleteDay = useCallback(() => {
    if (selectedDay == null || !data?.visit_data_by_day) return;
    const dayData = data.visit_data_by_day[String(selectedDay)];
    if (!dayData?.id) return;
    confirm({
      title: `${selectedDay}日の施術を削除してもよろしいですか？`,
      okText: 'OK',
      cancelText: 'キャンセル',
      okButtonProps: { danger: true },
      onOk: () =>
        deleteMutation.mutate(
          { delete_id: dayData.id },
          {
            onSuccess: () => {
              setSelectedDay(null);
              refetch();
            },
          }
        ),
    });
  }, [confirm, data?.visit_data_by_day, selectedDay, deleteMutation, refetch]);

  const handleOpenDetail = useCallback(() => {
    if (selectedDay == null || !data?.visit_data_by_day) return;
    const dayData = data.visit_data_by_day[String(selectedDay)];
    if (!dayData) return;
    setDetailForm({
      is_initial_inspection_fee: dayData.is_initial_inspection_fee === 1,
      is_acupuncture: dayData.is_acupuncture === 1,
      is_moxibustion: dayData.is_moxibustion === 1,
      electric_treatment: dayData.electric_treatment === 1,
      outcome_type_id: dayData.outcome_type_id || 0,
      massager_id: dayData.massager_id,
    });
    setDetailModalOpen(true);
  }, [data?.visit_data_by_day, selectedDay]);

  const handleDetailApply = useCallback(() => {
    setDetailModalOpen(false);
    setSelectedDay(null);
  }, []);

  const handleBulkDelete = useCallback(() => {
    if (!data?.visits?.length) return;
    const visitIds = data.visits.map(v => v.id);
    // Legacy A2 spec: use window.confirm (OK/Cancel) for bulk delete confirmation.
    if (window.confirm('施術（来院）を一括削除します。よろしいですか？')) {
      deleteMutation.mutate({ visit_ids: visitIds }, { onSuccess: () => refetch() });
    }
  }, [data?.visits, deleteMutation, refetch]);

  const handleRegister = useCallback(() => {
    if (!Number.isInteger(patientId) || !searchYearmonthDecoded || !data) return;
    const visitDataByDay = data.visit_data_by_day ?? {};
    const visits = Object.values(visitDataByDay).map(v => ({
      id: v.id,
      is_initial_inspection_fee: v.is_initial_inspection_fee ?? 0,
      is_acupuncture: v.is_acupuncture ?? 0,
      is_moxibustion: v.is_moxibustion ?? 0,
      electric_treatment: v.electric_treatment ?? 0,
      outcome_type_id: v.outcome_type_id ?? 0,
      massager_id: v.massager_id ?? null,
      visit_datetime_from: v.visit_datetime_from ?? '',
      visit_datetime_to: v.visit_datetime_from ?? '',
      previous_visit_id: v.previous_visit_id ?? -1,
    }));
    scheduleUpdateMutation.mutate({
      patient_id: patientId as number,
      yearmonth: searchYearmonthDecoded,
      visits,
    });
  }, [patientId, searchYearmonthDecoded, data, scheduleUpdateMutation]);

  const monthOptions = useMemo(() => {
    const opts: { value: string; label: string }[] = [];
    const now = new Date();
    for (let i = -6; i <= 1; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      opts.push({ value: `${y}-${m}`, label: `${y}年${d.getMonth() + 1}月` });
    }
    return opts;
  }, []);

  const columns: ColumnsType<ContinuationListRow> = [
    {
      title: '施術日',
      dataIndex: 'visit_datetime_from',
      key: 'visit_datetime_from',
      width: 100,
      render: (val: string) => (val ? val.slice(0, 10) : '—'),
    },
    { title: '保険種別', dataIndex: 'insurer_kind_name', key: 'insurer_kind_name', width: 100 },
    {
      title: '負担割合',
      dataIndex: 'patient_cost_rate_str',
      key: 'patient_cost_rate_str',
      width: 80,
    },
    {
      title: '初',
      dataIndex: 'is_initial_inspection_fee_mark',
      key: 'is_initial_inspection_fee_mark',
      width: 40,
    },
    {
      title: '自由診療',
      dataIndex: 'free_medical_treatment_name',
      key: 'free_medical_treatment_name',
      ellipsis: true,
    },
    { title: '領収', dataIndex: 'print_invoice_mark', key: 'print_invoice_mark', width: 40 },
    {
      title: '操作',
      key: 'action',
      width: 80,
      render: (_, record) => (
        <AppButton
          type="default"
          size="small"
          onClick={() => handleDelete(record.id)}
          className="text-red-600 hover:text-red-700"
        >
          削除
        </AppButton>
      ),
    },
  ];

  const visitDataByDayForCalendar = data?.visit_data_by_day ?? {};
  const daysInMonthForCalendar = data?.days_in_month ?? 31;

  const calendarDataSource = useMemo((): CalendarTableRow[] => {
    return CALENDAR_ROW_CONFIG.map(({ key, label, getVal, format }) => {
      const row: CalendarTableRow = { key, label };
      for (let d = 1; d <= daysInMonthForCalendar; d++) {
        const dayData = visitDataByDayForCalendar[String(d)];
        const hasVal = dayData && (format ? format(dayData) : getVal(dayData));
        row[String(d)] = hasVal ? (format && dayData ? format(dayData) : '〇') : '';
      }
      return row;
    });
  }, [visitDataByDayForCalendar, daysInMonthForCalendar]);

  const calendarColumns = useMemo((): ColumnsType<CalendarTableRow> => {
    const dayCols = Array.from({ length: daysInMonthForCalendar }, (_, i) => {
      const d = i + 1;
      const wd = getWeekday(searchYearmonthDecoded, d);
      const isSun = wd === 0;
      const isSat = wd === 6;
      return {
        title: (
          <div className="text-center">
            <div>{d}</div>
            <div
              className={`text-xs ${isSun ? 'text-red-500' : ''} ${isSat ? 'text-blue-600' : ''}`}
            >
              {WEEKDAY_LABELS_BY_INDEX[wd]}
            </div>
          </div>
        ),
        dataIndex: String(d),
        key: `day-${d}`,
        width: 32,
        align: 'center' as const,
        render: (val: string) => {
          const isSelected = selectedDay === d;
          const isSunCell = wd === 0;
          return (
            <span
              role="button"
              tabIndex={0}
              className={`inline-block min-w-[1.5rem] cursor-pointer rounded px-0.5 py-0.5 text-center text-sm ${
                isSelected
                  ? 'bg-blue-50 outline outline-2 outline-blue-500 outline-offset-[-2px]'
                  : 'hover:bg-gray-100'
              } ${!isSelected && isSunCell ? 'bg-red-50' : ''}`}
              onClick={() => setSelectedDay(prev => (prev === d ? null : d))}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSelectedDay(prev => (prev === d ? null : d));
                }
              }}
            >
              {val || ''}
            </span>
          );
        },
      };
    });
    return [
      {
        title: '',
        dataIndex: 'label',
        key: 'label',
        fixed: 'left',
        width: 80,
        className: 'bg-gray-50 font-medium',
      },
      ...dayCols,
    ];
  }, [daysInMonthForCalendar, searchYearmonthDecoded, selectedDay]);

  if (!Number.isInteger(patientId) || !searchYearmonthDecoded) {
    return (
      <div className="p-6">
        <p className="text-gray-500">パラメータが不正です。</p>
        <AppButton type="link" onClick={handleBack} className="mt-2">
          一覧に戻る
        </AppButton>
      </div>
    );
  }

  const patient = data?.patient;
  const visits = data?.visits ?? [];
  const visitDataByDay = data?.visit_data_by_day ?? {};
  const consentList = data?.consent_list ?? [];
  const massagerList = data?.massager_list ?? [];
  const outcomeTypes = data?.outcome_types ?? [];
  const daysInMonth = data?.days_in_month ?? 31;
  const yearMonthLabel = data?.year_month_label ?? searchYearmonthDecoded;
  const rezeptList = data?.rezept_list ?? [];
  const priceData = data?.price_data ?? {};

  return (
    <div className="pb-4">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <h1 className="text-xl font-semibold">施術入力編集（スケジュール）</h1>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-gray-600">施術年月</span>
          <AppSelect
            value={searchYearmonthDecoded}
            onChange={v => v && handleMonthChange(String(v))}
            options={monthOptions}
            className="w-36"
          />
          <AppButton type="default" onClick={handleBack}>
            ← 継続受診者一覧に戻る
          </AppButton>
        </div>
      </div>

      {<AppSpin spinning={isLoading} />}
      {isError && <p className="text-red-600">データの取得に失敗しました。</p>}
      {!isLoading && !isError && data && (
        <>
          {/* 患者情報 */}
          <CollapsibleCard title="患者情報" className="mb-4">
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <span>
                <span className="font-medium text-gray-600">患者氏名：</span>
                {patient?.name ?? '—'}
              </span>
              <span>
                <span className="font-medium text-gray-600">受診者番号：</span>[
                {patient?.serial_number ?? '—'}]
              </span>
              {data.division && (
                <span>
                  <span className="font-medium text-gray-600">請求区分：</span>[{data.division}]
                </span>
              )}
              <span>
                <span className="font-medium text-gray-600">対象月：</span>
                {yearMonthLabel}
              </span>
              <AppButton
                type="default"
                size="small"
                onClick={() => navigate(`/${tenant}/patients/edit/${patient?.id}`)}
              >
                保険証
              </AppButton>
            </div>
            {!data.has_health_insurance && (
              <p className="mt-2 font-medium text-red-600">※保険証が存在していません</p>
            )}
            {consentList.length === 0 && (
              <p className="mt-1 font-medium text-red-600">※同意書が存在していません</p>
            )}
          </CollapsibleCard>

          {/* 施術カレンダー */}
          <CollapsibleCard title="施術カレンダー" className="mb-4">
            <div className="overflow-x-auto">
              <AppTable<CalendarTableRow>
                rowKey="key"
                pagination={false}
                scroll={{ x: daysInMonth * 32 + 100 }}
                dataSource={calendarDataSource}
                columns={calendarColumns}
              />
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <AppButton
                type="default"
                size="small"
                onClick={handleDeleteDay}
                disabled={selectedDay == null || !visitDataByDay[String(selectedDay ?? '')]?.id}
              >
                日別削除
              </AppButton>
              <AppButton
                type="primary"
                size="small"
                onClick={handleOpenDetail}
                disabled={selectedDay == null || !visitDataByDay[String(selectedDay ?? '')]}
              >
                詳細
              </AppButton>
              <span className="ml-2 text-sm text-gray-500">
                転帰入力の場合は「施術内容」で日を選択し詳細をクリックしてください。
              </span>
            </div>
          </CollapsibleCard>

          {/* 施術一覧（表） */}
          <CollapsibleCard title={`施術一覧（${visits.length}件）`} className="mb-4">
            {visits.length === 0 ? (
              <p className="py-4 text-gray-500">この月の施術データはありません。</p>
            ) : (
              <AppTable<ContinuationListRow>
                rowKey="id"
                columns={columns}
                dataSource={visits}
                scroll={{ x: 600 }}
                pagination={false}
              />
            )}
          </CollapsibleCard>

          {/* 料金｜コメント */}
          <CollapsibleCard title="料金｜コメント" className="mb-4">
            {rezeptList.length === 0 ? (
              <p className="py-2 text-sm text-gray-500">当月のレセプト・料金データはありません。</p>
            ) : (
              <div className="space-y-4">
                {rezeptList.map((rezept: RezeptPriceRow) => {
                  const prices: PriceDataRow | undefined = priceData[rezept.id];
                  const relationshipLabel =
                    rezept.relationship_id === 1
                      ? '[本人]'
                      : rezept.relationship_id === 2
                        ? '[家族]'
                        : rezept.relationship_id === 3
                          ? '[67(本人)]'
                          : '';
                  const insuranceLabel =
                    rezept.insurance_type_id === 1
                      ? '[一般]'
                      : rezept.insurance_type_id === 2
                        ? '[後期高齢]'
                        : rezept.insurance_type_id === 3
                          ? '[生活保護]'
                          : '';
                  const dateRange =
                    rezept.qualification_date && rezept.expiration_date
                      ? `${rezept.qualification_date}～${rezept.expiration_date}`
                      : rezept.qualification_date
                        ? `${rezept.qualification_date}～`
                        : '';
                  return (
                    <div
                      key={rezept.id}
                      className="p-3 border border-gray-200 rounded bg-gray-50/50"
                    >
                      <div className="flex flex-wrap items-center gap-2 mb-2 text-sm">
                        <span>{relationshipLabel}</span>
                        <span>{insuranceLabel}</span>
                        {rezept.insurer_number && <span>[{rezept.insurer_number}]</span>}
                        {rezept.patient_cost_rate && <span>[{rezept.patient_cost_rate}割]</span>}
                        {dateRange && <span>[{dateRange}]</span>}
                        {rezept.all_status !== 0 && (
                          <span className="font-semibold text-red-600">※レセプト発行済</span>
                        )}
                        <span className="ml-auto">
                          提出年月{' '}
                          <span className="font-medium">{rezept.rezept_yearmonth_j || '—'}</span>
                        </span>
                      </div>
                      {prices && (
                        <AppTable<PriceTableRow>
                          rowKey="label"
                          dataSource={getPriceTableDataSource(prices)}
                          pagination={false}
                          columns={PRICE_TABLE_COLUMNS}
                        />
                      )}
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        <label className="text-sm font-medium shrink-0">
                          当月レセプト摘要欄内容
                        </label>
                        <span className="text-sm text-gray-700">{rezept.summary || '—'}</span>
                        <span className="ml-2 text-xs text-gray-400">
                          ※施術報告書交付料はこちらでは加算されておりません。
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CollapsibleCard>

          {/* 同意書情報 */}
          <CollapsibleCard title="同意書情報" className="mb-4">
            {consentList.length === 0 ? (
              <p className="py-4 text-gray-500">同意書データはありません。</p>
            ) : (
              <AppTable<ConsentRow>
                rowKey="id"
                dataSource={consentList}
                pagination={false}
                columns={CONSENT_TABLE_COLUMNS}
              />
            )}
          </CollapsibleCard>

          {/* 保険証情報 */}
          <CollapsibleCard title="保険証情報" className="mb-4">
            <div className="flex">
              <div className="w-[85px] shrink-0 flex flex-col gap-1">
                <AppFormLabel label="保険証情報" className="text-sm font-medium text-gray-700" />
                <AppFormLabel label="摘要欄内容" className="text-sm font-medium text-gray-700" />
              </div>
              <AppTextArea
                readOnly
                className="w-full min-h-[80px] bg-gray-50 text-sm text-gray-700 resize-y"
                value={typeof patient?.summary === 'string' ? patient.summary : ''}
                rows={4}
              />
            </div>
          </CollapsibleCard>

          {/* フッター操作 */}
          <div className="flex flex-wrap items-center justify-between gap-4 px-6 py-3 mt-4 bg-white border-t border-gray-200 rounded-b shadow">
            <AppButton type="default" onClick={handleBulkDelete} disabled={visits.length === 0}>
              一括削除
            </AppButton>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">
                データ修正後・エラー解消後は「当月再更新」にチェックして登録してください。
              </span>
              <AppButton
                type="primary"
                onClick={handleRegister}
                loading={scheduleUpdateMutation.isPending}
              >
                <i className="mr-2 fa fa-paper-plane-o" aria-hidden /> 登録
              </AppButton>
            </div>
          </div>
        </>
      )}

      {/* 詳細編集モーダル */}
      <AppModal
        isOpen={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        title={`${yearMonthLabel}${selectedDay ?? ''}日　詳細編集`}
        maxWidth={440}
        footer={
          <div className="flex justify-end gap-2">
            <AppButton type="default" onClick={() => setDetailModalOpen(false)}>
              キャンセル
            </AppButton>
            <AppButton type="primary" onClick={handleDetailApply}>
              適用
            </AppButton>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            転帰入力の場合は（治癒・中止・転医）を選択し適用してください。
          </p>
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <AppCheckbox
                checked={detailForm.is_initial_inspection_fee}
                onChange={e =>
                  setDetailForm(f => ({ ...f, is_initial_inspection_fee: e.target.checked }))
                }
              >
                初検料
              </AppCheckbox>
            </div>
            <div className="flex items-center gap-2">
              <AppCheckbox
                checked={detailForm.is_acupuncture}
                onChange={e => setDetailForm(f => ({ ...f, is_acupuncture: e.target.checked }))}
              >
                はり
              </AppCheckbox>
            </div>
            <div className="flex items-center gap-2">
              <AppCheckbox
                checked={detailForm.is_moxibustion}
                onChange={e => setDetailForm(f => ({ ...f, is_moxibustion: e.target.checked }))}
              >
                きゅう
              </AppCheckbox>
            </div>
            <div className="flex items-center gap-2">
              <AppCheckbox
                checked={detailForm.electric_treatment}
                onChange={e => setDetailForm(f => ({ ...f, electric_treatment: e.target.checked }))}
              >
                電(鍼・温)
              </AppCheckbox>
            </div>
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">転帰</label>
            <AppSelect
              value={detailForm.outcome_type_id || undefined}
              onChange={v => setDetailForm(f => ({ ...f, outcome_type_id: (v as number) || 0 }))}
              options={[
                { value: 0, label: '' },
                ...outcomeTypes.map(o => ({ value: o.id, label: o.name })),
              ]}
              className="w-full"
            />
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">施術担当者</label>
            <AppSelect
              value={detailForm.massager_id ?? undefined}
              onChange={v => setDetailForm(f => ({ ...f, massager_id: (v as number) ?? null }))}
              options={massagerList.map(m => ({
                value: m.id,
                label: `${m.serial_number}:${m.name}:${m.address1}${m.address2}${m.address3}`,
              }))}
              className="w-full"
              allowClear
            />
          </div>
        </div>
      </AppModal>
    </div>
  );
};

export default PatientContinuationEditPage;
